import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Crown, ChevronDown } from "lucide-react";

const MaskedLine = ({ children, delay, className = "" }) => (
  <span className={`block overflow-hidden ${className}`}>
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.2, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

export default function Hero({ settings }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative h-screen min-h-[660px] overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <div className="animate-hero-zoom h-full w-full">
          <img
            src={settings.hero_image || "/brand/stage-ramp.jpg"}
            alt="D'La Valentina grand stage"
            data-testid="hero-background-image"
            className="h-full w-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-black/55" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/80 via-transparent to-[#050505]" />
      </motion.div>

      <motion.div
        style={{ opacity: fade }}
        className="relative z-10 flex h-full flex-col items-center justify-center px-6 text-center"
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.6 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 1, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <Crown className="mx-auto mb-6 h-10 w-10 text-[#D4AF37]" strokeWidth={1} data-testid="hero-crown" />
        </motion.div>

        <MaskedLine delay={0.25}>
          <span data-testid="hero-tagline" className="font-script text-3xl md:text-5xl text-[#D4AF37]">
            {settings.hero_tagline || "The Queen of Pageants"}
          </span>
        </MaskedLine>

        <h1 className="font-display mt-4 font-medium leading-[0.92] tracking-tight">
          <MaskedLine delay={0.45}>
            <span data-testid="hero-title-line-1" className="text-[17vw] text-white md:text-[10vw] lg:text-[9vw]">
              {settings.hero_line1 || "D'LA"}
            </span>
          </MaskedLine>
          <MaskedLine delay={0.6}>
            <span data-testid="hero-title-line-2" className="gold-gradient-text text-[17vw] md:text-[10vw] lg:text-[9vw]">
              {settings.hero_line2 || "VALENTINA"}
            </span>
          </MaskedLine>
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-2xl text-sm font-light leading-relaxed tracking-wide text-neutral-300 md:text-base"
        >
          {settings.hero_subtitle}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.2, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col items-center gap-4 sm:flex-row"
        >
          <a
            href="/register"
            data-testid="hero-register-button"
            className="bg-[#D4AF37] px-10 py-4 text-xs font-medium uppercase tracking-[0.3em] text-black transition-all duration-300 hover:bg-[#F9F1CC]"
          >
            Enter the Pageant
          </a>
          <a
            href="#about"
            data-testid="hero-explore-button"
            className="border border-white/30 px-10 py-4 text-xs uppercase tracking-[0.3em] text-white transition-all duration-300 hover:border-[#D4AF37] hover:text-[#D4AF37]"
          >
            Discover the House
          </a>
        </motion.div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2"
      >
        <ChevronDown className="h-6 w-6 animate-bounce text-[#D4AF37]/70" strokeWidth={1} />
      </motion.div>
    </section>
  );
}
